import { useMemo } from 'react'
import { Button, Input, Spinner, Textarea, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AgamiMediaFileSelector from '../ViewUpload/AgamiMediaFileSelector'
import AgamiExperienceSelector from '../ViewUpload/AgamiExperienceSelector'
import AgamiVisibilitySelector from '../ViewUpload/AgamiVisibilitySelector'
import AgamiPreGeneratedSignedUrl from '../components/AgamiPreGeneratedSignedUrl'
import ProposalBanner from '../components/ProposalBanner'
import LocalizationsEditor from '../components/LocalizationsEditor/LocalizationsEditor'
import AgamiMediaTypeSelector from '../components/AgamiMediaTypeSelector'
import AgamiActivationBlocks from './AgamiActivationBlocks'
import { useAgamiFormV2, AgamiFormV2Mode } from './useAgamiFormV2'
import { useAccessibleExperiences } from '../hooks/useAccessibleExperiences'
import { useSessionContext } from '../contexts/SessionContext'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { CAPABILITIES } from '../utils/constants/capabilities.constants'
import { AGAMI_GLOBAL_EXPERIENCE_ID, SPOT_MEDIA_TYPES } from '../utils/constants/db.constants'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'
import { AgamiSpot } from '../types/supabase'

interface AgamiFormV2Props {
  agami?: AgamiSpot
  mode: AgamiFormV2Mode
  proposeToExperienceId?: string
  onSaved: (outcome: { created: boolean; isDraft: boolean }) => void
  /** Back to the context one came from (06.4); browser history when absent. */
  onCancel?: () => void
}

/**
 * The v2 form (06.1): orchestration only — every field is a reused component,
 * and state/validation/submit live in useAgamiFormV2. Coexists with
 * UploadAgamiInterface without sharing state or conditionals: the v1 protects
 * every venue in production, this one hosts what the next epics add (05.5's
 * banner is already here; activation blocks arrive with 06.2, optional media
 * with 07, languages with 08).
 */
const AgamiFormV2: React.FC<AgamiFormV2Props> = ({
  agami,
  mode,
  proposeToExperienceId,
  onSaved,
  onCancel,
}) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { darkMode } = useDarkMode()
  const navigate = useNavigate()

  const {
    formData,
    setFormData,
    handleDataChange,
    mediaFile,
    setMediaFile,
    isPublic,
    setIsPublic,
    showMakePublicOption,
    uploading,
    checkInputIsValid,
    localizations,
    submit,
  } = useAgamiFormV2({ agami, mode, proposeToExperienceId, onSaved })

  // Owned experiences plus those with a DIRECT-write capability
  // (attach_own_agami, the contributor's). Experiences where the user can only
  // propose (submit_draft) stay out of author mode: proposing has its own mode
  // with ONE fixed destination, because is_draft lives on the agami and mixing
  // a propose-target with a publish-target would draft the word everywhere.
  const { experiences: accessibleExperiences } = useAccessibleExperiences()

  const selectableExperiences = useMemo(
    () =>
      (accessibleExperiences ?? []).filter(
        (exp) =>
          exp.id !== user?.defaultExperienceId &&
          exp.id !== AGAMI_GLOBAL_EXPERIENCE_ID &&
          (exp.role === 'owner' || exp.capabilities.includes(CAPABILITIES.ATTACH_OWN_AGAMI))
      ),
    [accessibleExperiences, user?.defaultExperienceId]
  )

  const proposeDestination = useMemo(
    () => (accessibleExperiences ?? []).find((exp) => exp.id === proposeToExperienceId),
    [accessibleExperiences, proposeToExperienceId]
  )

  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)
  const canDraftAgami = useFeatureAuth(FEATURES.DRAFT_AGAMI)

  const hasPersistedMedia = Boolean(agami?.audio_content_path || localizations.hasPersistedMedia)
  const disabledMediaTypes = hasPersistedMedia
    ? [SPOT_MEDIA_TYPES.AUDIO, SPOT_MEDIA_TYPES.VIDEO, SPOT_MEDIA_TYPES.NONE].filter(
        (type) => type !== formData.content_media_type
      )
    : []

  const changeMediaType = (mediaType: number) => {
    if (mediaType === formData.content_media_type) return
    setMediaFile(null)
    localizations.clearMediaFiles()
    handleDataChange('content_media_type', mediaType)
  }

  const confirmButtonLabel = useMemo(() => {
    if (mode === 'propose') return t('uploadV2.propose.submit')
    return isPublic
      ? t('uploadAgamiInterface.confirmAndMakePublic')
      : t('uploadAgamiInterface.confirmAndSave')
  }, [mode, isPublic, t])

  const draftButtonLabel = useMemo(() => {
    if (!agami) return t('uploadAgamiInterface.saveAsDraft')
    if (!agami?.is_draft) return t('uploadAgamiInterface.convertToDraft')
    return t('uploadAgamiInterface.saveEditsAndKeepAsDraft')
  }, [agami, t])

  return (
    <div className="w-full lg:w-11/12 my-10" data-testid="agami-form-v2">
      {agami && (
        <div className="flex gap-2 mb-4">
          <Typography variant="small">
            {t('uploadAgamiInterface.created')}: <strong>{formatDateTimeString(agami?.inserted_at)}</strong>
          </Typography>
          <Typography variant="small">
            {' | '}
            {t('uploadAgamiInterface.lastUpdate')}: <strong>{formatDateTimeString(agami?.updated_at)}</strong>
          </Typography>
        </div>
      )}

      {/* 05.5 lives here too: whoever opens a proposal is deciding, not editing.
          Renders nothing without a pending submission. */}
      {agami && <ProposalBanner agami={agami} />}

      <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
        {/* The TITLE stays outside the language tabs (R2, D6): a word is one
            word, and putting the field inside the editor would open the door
            to the per-language title the requirement excludes. */}
        <div className="w-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4">
          <Typography className="font-bold text-primary text-xl font-barlow">
            {t('uploadAgamiInterface.details')}
          </Typography>
          <Input
            className="w-full"
            value={formData.spotName}
            onChange={(e) => handleDataChange('spotName', e.currentTarget.value)}
            type="text"
            label={t('uploadAgamiInterface.nameLabel')}
            required
            disabled={uploading}
            data-testid="agami-name-input"
            {...(darkMode && { color: 'white' })}
          />
          {/* A proposal carries no media and no localizations: the channel
              (submit-agami-draft) accepts a plain description, nothing more. */}
          {mode === 'propose' && (
            <Textarea
              className="w-full text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
              rows={5}
              value={formData.description}
              onChange={(e) => handleDataChange('description', e.currentTarget.value)}
              label={t('uploadAgamiInterface.descriptionLabel')}
              disabled={uploading}
              data-testid="agami-description-input"
              {...(darkMode && { color: 'blue-gray' })}
              labelProps={{
                className:
                  'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
              }}
            />
          )}
        </div>

        {mode === 'author' && (
          <AgamiMediaTypeSelector
            value={formData.content_media_type}
            onChange={changeMediaType}
            disabledTypes={disabledMediaTypes}
            disabled={uploading || localizations.isLoading}
          />
        )}

        {/* Per-language content (08.2): the MAIN tab renders the same fields
            the form always saved to the spot's base columns — the editor never
            writes them, which is what keeps a monolingual agami the degenerate
            case, indistinguishable from the v1's record. */}
        {mode === 'author' && (
          <LocalizationsEditor
            baseLanguage={formData.base_language}
            onBaseLanguageChange={(language) => handleDataChange('base_language', language)}
            mainHasContent={Boolean(formData.description?.trim() || mediaFile || agami?.audio_content_path)}
            mainContent={
              <div className="grid grid-cols-2 gap-4" data-testclass="language-content-fields">
                <AgamiMediaFileSelector
                  agami={agami}
                  mediaFile={mediaFile}
                  setMediaFile={setMediaFile}
                  formData={formData}
                  handleDataChange={handleDataChange}
                  showMediaTypeSelector={false}
                />
                <div
                  className="w-full h-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4"
                  data-testclass="language-description-card"
                >
                  <Typography className="font-bold text-primary text-xl font-barlow">
                    {t('uploadAgamiInterface.descriptionLabel')}
                  </Typography>
                  <Textarea
                    className="w-full text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
                    rows={5}
                    value={formData.description}
                    onChange={(e) => handleDataChange('description', e.currentTarget.value)}
                    label={t('uploadAgamiInterface.descriptionLabel')}
                    disabled={uploading}
                    data-testid="agami-description-input"
                    {...(darkMode && { color: 'blue-gray' })}
                    labelProps={{
                      className:
                        'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
                    }}
                  />
                </div>
              </div>
            }
            drafts={localizations.drafts}
            contentMediaType={formData.content_media_type}
            uploading={uploading}
            onAddLanguage={localizations.addLanguage}
            onRemoveLanguage={localizations.removeLanguage}
            onDescriptionChange={localizations.setDescription}
            onMediaFileChange={localizations.setMediaFile}
          />
        )}

        {/* ONE and FIXED destination in propose mode — a schema consequence, not
            a product whim: is_draft lives on geoxp_spots, so an agami is a draft
            absolutely. Choosing a propose-target and a publish-target together
            would leave it drafted (invisible) in both. */}
        {mode === 'propose' && (
          <div
            className="flex flex-col gap-2 items-start bg-white dark:bg-primary rounded-lg p-4 shadow-sm"
            data-testid="propose-destination"
          >
            <Typography className="font-bold text-primary text-lg font-barlow">
              {t('uploadV2.propose.destinationTitle')}
            </Typography>
            <Typography variant="small">
              {t('uploadV2.propose.destinationBody', {
                experience: proposeDestination?.name ?? '',
              })}
            </Typography>
          </div>
        )}

        {/* The activation blocks (06.2): a proposal has none — its activation
            type is the channel's business (custom, set by the RPC), not the
            proposer's. */}
        {mode === 'author' && (
          <AgamiActivationBlocks
            formData={formData}
            setFormData={setFormData}
            handleDataChange={handleDataChange}
            checkInputIsValid={checkInputIsValid}
            uploading={uploading}
            isPublic={isPublic}
            spotId={agami?.id}
          />
        )}

        {mode === 'author' && selectableExperiences.length > 0 && (
          <AgamiExperienceSelector
            userExperiences={selectableExperiences}
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {showMakePublicOption && (
          <AgamiVisibilitySelector isPublic={isPublic} setIsPublic={setIsPublic} formData={formData} />
        )}

        {experimentalFeats && agami && <AgamiPreGeneratedSignedUrl agami={agami} />}

        {/* No Approve button here either (05.5, D15): publishing IS the
            approval, and the one place that passes isDraft: false is this one. */}
        <div className="flex flex-col md:flex-row gap-4 mt-8">
          <Button
            type="button"
            className="w-full md:w-auto"
            disabled={uploading || !formData.spotName.trim()}
            onClick={() => submit({ isDraft: false })}
            data-testid="agami-v2-publish-btn"
          >
            {uploading ? <Spinner className="h-4 w-4 mx-auto" /> : confirmButtonLabel}
          </Button>

          {mode === 'author' && canDraftAgami && (
            <Button
              color={darkMode ? 'white' : ('primary' as colors)}
              type="button"
              variant="outlined"
              className="w-full md:w-auto"
              disabled={uploading || !formData.spotName.trim()}
              onClick={() => submit({ isDraft: true })}
              data-testid="agami-v2-draft-btn"
            >
              {draftButtonLabel}
            </Button>
          )}

          <Button
            type="button"
            variant="text"
            className="w-full md:w-auto"
            color="red"
            onClick={() => (onCancel ? onCancel() : navigate(-1))}
            disabled={uploading}
            data-testid="agami-v2-cancel-btn"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default AgamiFormV2
